import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { injectStyles } from 'injectStyles';
import * as analytics from 'analytics';

import { BreadcrumbsSchema, FaqSchema, PageTypeSchema } from 'components/Schema';
import PageHeader from 'components/PageHeader';
import PageContent from 'components/PageContent';
import LoadingPage from 'components/pages/status/LoadingPage';
import NotFoundPage from 'components/pages/status/NotFoundPage';
import FaqCategoryList from 'components/FaqCategoryList';
import ChevronBasicIcon from 'components/svg/ChevronBasic';
import FaqAnswer from 'components/FaqAnswer';

import { faqContentTypes, FAQ_LANDING_SLUG } from 'actions/actions-const';

import { VRAGEN_PAGE_TITLE } from 'globalConst/const';
import { SCHEMA_PAGE_TYPE } from 'globalConst/schema-const';

class FaqCategoryPage extends PureComponent {
  static defaultProps = {
    hidePlayer: false,
    hideSearch: false,
    isError: false,
    isLoading: false,
    questions: null,
    title: null,
    description: null,
  };

  static propTypes = {
    classes: PropTypes.object.isRequired,
    hidePlayer: PropTypes.bool,
    hideSearch: PropTypes.bool,
    forceMiniPlayerState: PropTypes.func.isRequired,
    isError: PropTypes.bool,
    isLoading: PropTypes.bool,
    questions: PropTypes.array,
    setSearchHidden: PropTypes.func.isRequired,
    slug: PropTypes.string.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
  };

  state = {
    openQuestion: null,
  };

  componentDidUpdate() {
    const { hidePlayer, hideSearch } = this.props;

    this.props.forceMiniPlayerState(hidePlayer);
    this.props.setSearchHidden(hideSearch);
  }

  componentWillUnmount() {
    const { hidePlayer, hideSearch } = this.props;
    if (hidePlayer) {
      this.props.forceMiniPlayerState(false);
    }
    if (hideSearch) {
      this.props.setSearchHidden(false);
    }
  }

  open = (id, question) =>
    this.setState(
      (prevState) => ({
        openQuestion: prevState.openQuestion === id ? null : id,
      }),
      () => this.state.openQuestion && analytics.faqExpand(question)
    );

  render() {
    const { classes, isLoading, isError, questions, slug, title, description } = this.props;

    const { openQuestion } = this.state;

    if (isLoading) {
      return <LoadingPage />;
    }

    if (isError) {
      return <NotFoundPage />;
    }

    return (
      <div className={classes.category}>
        <PageTypeSchema type={SCHEMA_PAGE_TYPE.COLLECTION_PAGE} metadata={{ title, description }} />
        <PageHeader {...this.props} />
        <BreadcrumbsSchema level2={VRAGEN_PAGE_TITLE} level3={title} />
        <PageContent>
          {questions &&
            questions.map(({ id, question, answerContent }) => (
              <div key={id} className={classes.accordionItem}>
                <div className={classes.questionWrapper} onClick={() => this.open(id, question)} role="presentation">
                  <p
                    className={classnames(classes.question, classes.text, {
                      [classes.darkColor]: openQuestion === id,
                    })}
                  >
                    {question}
                  </p>
                  <button className={classes.button} type="button">
                    <ChevronBasicIcon
                      className={classnames(classes.icon, {
                        [classes.iconUp]: openQuestion === id,
                      })}
                    />
                  </button>
                </div>
                <div
                  className={classnames(classes.answer, classes.text, {
                    [classes.open]: openQuestion === id,
                  })}
                >
                  {openQuestion === id && (
                    <FaqAnswer answerContent={answerContent} questionSlug={slug} question={question} />
                  )}
                </div>
              </div>
            ))}
          <FaqSchema faqs={questions} />
          <FaqCategoryList contentType={faqContentTypes.LANDING} slug={FAQ_LANDING_SLUG} current={slug} />
        </PageContent>
      </div>
    );
  }
}

const styles = (theme) => ({
  category: {
    margin: 'auto',
    maxWidth: 780,
  },
  accordionItem: {
    boxShadow: 'inset 0 -1px 0 0 rgba(0,0,0,.1)',
  },
  questionWrapper: {
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  text: {
    fontSize: 18,
    lineHeight: 1.33,
    color: theme.palette.text.secondary,
    '& p': {
      fontSize: 18,
      lineHeight: 1.33,
      color: theme.palette.text.secondary,
    },
  },
  question: {
    flex: 1,
    padding: '25px 0',
    margin: 0,
    '&:hover': {
      color: theme.palette.navigation.highlight,
    },
  },
  darkColor: {
    color: theme.palette.text.primary,
  },
  button: {
    color: theme.palette.text.secondary,
    border: 'none',
    borderRadius: 0,
    textDecoration: 'none',
    background: 'none',
  },
  icon: {
    width: 6,
    transform: 'rotate(90deg)',
  },
  iconUp: {
    transform: 'rotate(-90deg)',
  },
  answer: {
    height: 0,
    overflow: 'hidden',
  },
  open: {
    height: 'auto',
    paddingBottom: 40,
  },
});

export default injectStyles(styles)(FaqCategoryPage);
